import { SearchOptions, SearchResponse } from '@algolia/client-search';
import { DeviceType, Product, AlgoliaIndiceName } from '@packages/common';
import { SearchBase } from './searchBase';
import { AlgoliaSearchParams } from '../types';
import { excludedPartnersSearch } from './excludedPartnersSearch';

export const defaultProductsAttributesToRetrieve: (keyof Product)[] = [
  'createdAt',
  'clickoutUrl',
  'productId',
  'category',
  'display',
  'locations',
  'partner',
  'images',
  'title',
  'price',
  'shipping',
  'objectID',
  'features',
  'voucher',
  'flags',
  'rating',
];

interface ProductSearchQueryParams<T> extends Omit<SearchOptions, 'attributesToRetrieve'> {
  attributesToRetrieve?: T[];
}

interface ProductSearchParams<T extends string> extends AlgoliaSearchParams {
  queryParameters: ProductSearchQueryParams<T>;
}

export type ProductSearch = <T extends keyof Product>(
  params: ProductSearchParams<T>,
) => Promise<SearchResponse<Pick<Product, T>> | null>;

let excludedPartnersFilters: string | null = null;

export const getExcludedPartnersFilter = async (algoliaSearchBase: SearchBase) => {
  if (excludedPartnersFilters) {
    return excludedPartnersFilters;
  } else {
    const response = await excludedPartnersSearch(algoliaSearchBase);
    const data = response !== null ? response.hits.map((hit) => `partner.id:${hit.objectID}`).join(' AND NOT ') : null;
    /*eslint-disable require-atomic-updates */
    excludedPartnersFilters = data;
    return data;
  }
};

export const productSearch =
  (searchBase: SearchBase, userToken: string, deviceType = DeviceType.desktop): ProductSearch =>
  async (params) => {
    const { indiceName = AlgoliaIndiceName.PRODUCTS, queryParameters } = params;

    const indice =
      indiceName === AlgoliaIndiceName.PRODUCTS && deviceType === DeviceType.mobile
        ? AlgoliaIndiceName.PRODUCTS_MOBILE
        : indiceName;

    const excludedPartners = await getExcludedPartnersFilter(searchBase);
    const excludedPartnersFilter = `NOT ${excludedPartners}`;

    const filters = [`display.${deviceType}:true`, queryParameters.filters]
      .filter((item) => item != null)
      .filter((item) => item !== '')
      .filter((item) => !item?.includes('undefined'))
      .filter((item) => !item?.includes('null'))
      .join(' AND ');

    return await searchBase<Product>(indice, {
      attributesToRetrieve: defaultProductsAttributesToRetrieve,
      ...queryParameters,
      filters: [filters, excludedPartnersFilter].join(' AND '),
      userToken,
    });
  };
